import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import AstroImage from '@/components/ui/custom/AstroImage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton } from '@/components/ui/daisy';
import CourseProgressIndicator from '@/components/student-dashboard/CourseProgressIndicator';
import { useStudentDashboard } from '@/data/context/StudentDashboardContext';
import { getBannerImg } from '@/utils/coursePath';
import { ArrowPathIcon } from '@heroicons/react/20/solid';
import CourseSelectDrawer from './CourseSelectDrawer';
import useToggle from '@/data/hook/useToggle';

const CARD_CLASS_NAME =
  'flex min-w-0 items-center gap-3 rounded-2xl bg-base-200 p-4 max-h-auto md:max-h-12';

export default function ActiveCourseSelect() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.courseSelect',
  });
  const { activeCourseProgress, courseProgresses, isLoading } =
    useStudentDashboard();
  const { isOpen, open, close } = useToggle();

  if (isLoading)
    return <Skeleton className="h-12 w-full md:w-xs xl:w-sm rounded-2xl" />;

  if (!activeCourseProgress) return null;

  const hasMultipleCourses = courseProgresses.length > 1;

  const content = (
    <Fragment>
      <AstroImage
        src={getBannerImg(activeCourseProgress.coursePath)}
        alt=""
        className="size-6.5 shrink-0 rounded-full object-cover"
      />
      <span className="grow truncate text-sm font-medium text-base-content leading-5">
        {activeCourseProgress.coursePath.course.name}
      </span>
      <CourseProgressIndicator
        progress={activeCourseProgress.progress}
        progressClassName="w-24 shrink-0"
      />
    </Fragment>
  );

  return (
    <Fragment>
      <ConditionalRenderer condition={!hasMultipleCourses}>
        <div data-testid="activeCourseSelect" className={CARD_CLASS_NAME}>
          {content}
        </div>
      </ConditionalRenderer>
      <ConditionalRenderer condition={hasMultipleCourses}>
        <button
          type="button"
          data-testid="activeCourseSelect"
          onClick={open}
          className={twJoin(
            CARD_CLASS_NAME,
            'cursor-pointer text-left transition-colors hover:bg-base-300',
          )}
        >
          {content}
          <span className="sr-only">{t('changeCourse')}</span>
          <ArrowPathIcon className="size-5 shrink-0 text-base-content" />
        </button>
        <CourseSelectDrawer isOpen={isOpen} onClose={close} />
      </ConditionalRenderer>
    </Fragment>
  );
}
